import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography } from "@mui/material";
import {
  AddAPhoto as AddAPhotoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

// Componentes SUI
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import FileDialog from "components/FileDialog"; // Importar FileDialog para el modal

// API
import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";

const CartaLaboral = ({ procesoId, identificador, setError }) => {
  const [documentoUrl, setDocumentoUrl] = useState("");
  const [hasDocumento, setHasDocumento] = useState(false);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false); // Estado para el modal de PDF
  const FILE_NAME = "carta_laboral.pdf";
  const ANEXO_TYPE = "CARTA_LABORAL";

  useEffect(() => {
    const checkDocumento = async () => {
      try {
        const response = await BucketApi.checkSolicitudFileExists(FILE_NAME, identificador);
        setHasDocumento(response.data.exists);
        if (response.data.exists) {
          //const urlResponse = await BucketApi.getSolicitudFileUrl(FILE_NAME, identificador);
          const urlResponse = await BucketApi.getSolicitudFileUrlFresh(FILE_NAME, identificador);
          setDocumentoUrl(urlResponse.data.file_url);
        }
      } catch (error) {
        console.error("Error verificando la Carta Laboral:", error);
        setHasDocumento(false);
      }
    };
    if (identificador) {
      checkDocumento();
    }
  }, [identificador]);

  const handleDocumentoChange = async (event) => {
    const file = event.target.files[0];
    if (!file || !identificador) return;
    setIsLoading(true);
    try {
      if (hasDocumento) {
        const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(procesoId, {
          tipo_anexo: ANEXO_TYPE,
        });
        const anexo = anexoResponse.data;
        if (anexo != undefined && !anexoResponse.data.error) {
          await ProcesosApi.eliminarAnexoProceso(procesoId, anexo.id);
        }
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_name", FILE_NAME);
      formData.append("file_type", "solicitud");
      formData.append("proceso_id", identificador);
      formData.append("registrar_anexo", true); // Indicar que se debe registrar como anexo
      formData.append("tipo_anexo", ANEXO_TYPE);
      formData.append("nombre_anexo", "Carta Laboral");
      formData.append("incluir_en_pdf", "True");
      const response = await BucketApi.uploadFile(formData);
      if (response.data?.file_url) {
        setHasDocumento(true);
        setDocumentoUrl(response.data.file_url);
      }
    } catch (error) {
      console.error("Error subiendo la Carta Laboral:", error);
      setError(obtenerMensajeError(error, "Error al subir la Carta Laboral"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDocumento = async () => {
    if (!identificador || !window.confirm("¿Está seguro(a) de eliminar la Carta Laboral?")) return;
    setIsLoading(true);
    try {
      const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(procesoId, {
        tipo_anexo: ANEXO_TYPE,
      });
      const anexo = anexoResponse.data;
      if (anexo != undefined && !anexoResponse.data.error) {
        await ProcesosApi.eliminarAnexoProceso(procesoId, anexo.id);
      }
      setHasDocumento(false);
      setDocumentoUrl("");
    } catch (error) {
      console.error("Error eliminando la Carta Laboral:", error);
      setError(obtenerMensajeError(error, "Error al eliminar la Carta Laboral"));
    } finally {
      setIsLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <SuiBox>
      <SuiTypography variant="h6" fontWeight="medium" mb={2}>
        Carta Laboral
      </SuiTypography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {hasDocumento ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 150,
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body1" color="textSecondary" align="center">
              Carta Laboral Cargada
            </Typography>
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                display: "flex",
                gap: 1,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                padding: "4px",
                borderRadius: "4px",
              }}
            >
              <IconButton size="small" onClick={triggerFileInput} color="info">
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={handleDeleteDocumento} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
            <Typography
              variant="caption"
              color="primary"
              sx={{ cursor: "pointer", mt: 2 }}
              onClick={() => setShowPdfViewer(true)} // Mostrar el modal al hacer clic
            >
              Ver Documento
            </Typography>
          </Box>
        ) : (
          <Box
            onClick={triggerFileInput}
            sx={{
              width: "100%",
              height: 150,
              border: "2px dashed #ccc",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backgroundColor: "#f8f9fa",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "#f0f0f0",
              },
            }}
          >
            <AddAPhotoIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
            <Typography color="textSecondary" align="center" sx={{ px: 2 }}>
              Click para subir la Carta Laboral
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
              Formato requerido: PDF
            </Typography>
          </Box>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleDocumentoChange}
        style={{ display: "none" }}
      />

      {isLoading && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography color="textSecondary">Procesando...</Typography>
        </Box>
      )}

      {/* Modal para visualizar el PDF */}
      <FileDialog
        open={showPdfViewer}
        onClose={() => setShowPdfViewer(false)}
        title={`Carta Laboral - ${identificador}`}
        content={
          <embed
            src={documentoUrl}
            type="application/pdf"
            width="100%"
            height="500px"
            style={{ border: "none" }}
          />
        }
      />
    </SuiBox>
  );
};

CartaLaboral.propTypes = {
  procesoId: PropTypes.number.isRequired,
  identificador: PropTypes.string,
  setError: PropTypes.func.isRequired,
};

export default CartaLaboral;
